<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Card from '@uiKit/components/Card.vue'
import FormField from '@uiKit/components/FormField.vue'
import Input from '@uiKit/elements/Input.vue'
import NumberInput from '@uiKit/elements/NumberInput.vue'
import Select from '@uiKit/elements/Select.vue'
import SelectButton from '@uiKit/elements/SelectButton.vue'
import Textarea from '@uiKit/elements/Textarea.vue'
import DatePicker from '@uiKit/elements/DatePicker.vue'
import ToggleSwitch from '@uiKit/elements/ToggleSwitch.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Chip from '@uiKit/elements/Chip.vue'
import {
  FEE_LIFECYCLE_STAGE_LABEL,
  FEE_LIFECYCLE_STAGE_OPTIONS,
  FEE_PARTY_ROLE_LABEL,
  FEE_PARTY_ROLE_OPTIONS,
  FEE_RECURRENCE_OPTIONS,
  FEE_UNIT_BASIS_OPTIONS,
  FEE_UNIT_BASIS_UNIT,
  FEE_VALUE_BASIS_DESCRIPTION,
  FEE_VALUE_TYPE,
  FEE_VALUE_TYPE_OPTIONS,
  feeValueBasisOptionsFor,
  requiresUnitBasis,
} from '@shared/constants/fees'
import type { SelectOption } from '@shared/types'
import type { FeeDefinition, FeeDefinitionFormValues } from '../types/feeTypes'
import {
  emptyFeeFormValues,
  feeCodeFromName,
  normalizeFeeCodeInput,
  resolveUnitBasisFor,
  resolveValueBasisFor,
  toFeeFormValues,
  validateFeeForm,
  type FeeCodeInForce,
  type FeeFormErrors,
} from '../utils/feeFormUtils'

const props = defineProps<{
  /** Null opens the form on a brand-new definition. */
  fee: FeeDefinition | null
  /** Every market a fee can be scoped to, `{ label: name, value: id }`. */
  markets: SelectOption[]
  /** Every `(market, code)` pair already in force, so a clash is caught here. */
  codesInForce: FeeCodeInForce[]
  isLoading: boolean
}>()

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'save', values: FeeDefinitionFormValues): void
}>()

const isSupersede = computed(() => props.fee !== null)
const form = ref<FeeDefinitionFormValues>(
  props.fee ? toFeeFormValues(props.fee) : emptyFeeFormValues(),
)
const hasAttemptedSave = ref(false)

const errors = computed<FeeFormErrors>(() =>
  validateFeeForm(form.value, isSupersede.value, props.codesInForce),
)
const visibleErrors = computed<FeeFormErrors>(() => (hasAttemptedSave.value ? errors.value : {}))
const isPercentage = computed(() => form.value.valueType === FEE_VALUE_TYPE.PERCENTAGE)
const isRatePerUnit = computed(() => requiresUnitBasis(form.value.valueType))

// The rate is quoted per unit of the chosen area, so the amount field has to say
// which unit — "Amount (AED)" on a per-sqm rate reads as the whole-property cost.
const valueFieldLabel = computed<string>(() => {
  if (isPercentage.value) return 'Rate (%)'
  if (isRatePerUnit.value && form.value.unitBasis !== null) {
    return `Rate (AED / ${FEE_UNIT_BASIS_UNIT[form.value.unitBasis]})`
  }
  return 'Amount (AED)'
})

// `Select` speaks strings; clearing the picker reads back as no area chosen.
const unitBasisModel = computed<string>({
  get: () => form.value.unitBasis ?? '',
  set: (value) => {
    form.value.unitBasis = (value || null) as FeeDefinitionFormValues['unitBasis']
  },
})

// "All markets" is a real choice, not an empty one: it stores null in
// `fees_master.market_id`, which is the system default every market inherits.
// `Select` cannot model null, so the sentinel is an empty string.
const ALL_MARKETS = ''

const marketOptions = computed<SelectOption[]>(() => [
  { label: 'All markets', value: ALL_MARKETS },
  ...props.markets,
])

const marketModel = computed<string>({
  get: () => form.value.marketId ?? ALL_MARKETS,
  set: (value) => {
    form.value.marketId = value === ALL_MARKETS ? null : value
  },
})

// Named for the reader, not the record: a null market id is not "no market".
const marketLabel = computed<string>(() => {
  if (form.value.marketId === null) return 'All markets'
  const market = props.markets.find((option) => option.value === form.value.marketId)
  return market ? String(market.label) : 'Market-specific'
})

// `Select` speaks strings; the field is nullable until an admin picks a basis,
// and an empty selection reads back as no basis.
const valueBasisModel = computed<string>({
  get: () => form.value.valueBasis ?? '',
  set: (value) => {
    form.value.valueBasis = (value || null) as FeeDefinitionFormValues['valueBasis']
  },
})

// A rate can be taken against any base; a flat amount only offers the two that
// say whose share is owed, plus `flat_only` for charged-whole.
const valueBasisOptions = computed(() => feeValueBasisOptionsFor(form.value.valueType))

// The chosen basis carries the whole rule, and neither label is self-explanatory
// on its own — so the field explains the selection rather than the field.
const valueBasisHelperText = computed<string>(() =>
  form.value.valueBasis === null
    ? 'What the fee is measured against.'
    : FEE_VALUE_BASIS_DESCRIPTION[form.value.valueBasis],
)

watch(
  () => props.fee,
  (fee) => {
    form.value = fee ? toFeeFormValues(fee) : emptyFeeFormValues()
    hasAttemptedSave.value = false
    isCodeHandTyped.value = false
  },
)

// The code follows the name until the admin takes it over. Naming the fee is
// the decision; the code is the machine-readable form of that name, and an
// admin who wants a different one just types it — from then on the name no
// longer overwrites it.
const isCodeHandTyped = ref(false)

const codeModel = computed<string>({
  get: () => form.value.code,
  set: (value) => {
    isCodeHandTyped.value = true
    form.value.code = normalizeFeeCodeInput(value)
  },
})

watch(
  () => form.value.name,
  (name) => {
    if (isSupersede.value || isCodeHandTyped.value) return
    form.value.code = feeCodeFromName(name)
  },
)

// Neither the basis nor the area is a free choice — both follow the value type,
// so an admin who flips percentage → flat → rate never lands on a combination
// the backend refuses.
watch(
  () => form.value.valueType,
  () => {
    form.value.valueBasis = resolveValueBasisFor(form.value)
    form.value.unitBasis = resolveUnitBasisFor(form.value)
  },
)

// The two role dropdowns are one decision — who owes whom — so the selection
// reads back as a single label instead of two unrelated fields.
const partyRoleLabel = computed<string>(
  () =>
    `${FEE_PARTY_ROLE_LABEL[form.value.paidByRole]} \u2192 ${FEE_PARTY_ROLE_LABEL[form.value.paidToRole]}`,
)

function handleSubmit(): void {
  hasAttemptedSave.value = true
  if (Object.keys(errors.value).length > 0) return
  emit('save', form.value)
}
</script>

<template>
  <Box class="animate-fade-in">
    <Box flex class="align-items-center gap-3 mb-5">
      <Button icon="pi pi-arrow-left" variant="text" class="flex-shrink-0" @click="emit('back')" />
      <Box>
        <Text type="page-title">
          {{ isSupersede ? `Edit fee: ${props.fee?.name}` : 'New fee definition' }}
        </Text>
        <Text type="body" color="muted" class="mt-1">
          {{
            isSupersede
              ? 'Saving closes the version in force and opens a new one.'
              : 'Define what is charged, who pays it and who receives it.'
          }}
        </Text>
      </Box>
    </Box>

    <Box class="grid">
      <Box class="col-12 lg:col-8">
        <Card variant="elevated" class="form-card">
          <Box v-if="isSupersede" class="mb-5 readonly-section">
            <Text type="caption-sm" weight="bold" transform="uppercase" class="field-label">
              Identity — carries over to the new version
            </Text>
            <Box flex class="gap-4 flex-wrap">
              <Box>
                <Text type="caption" color="muted">Market</Text>
                <Text type="body" weight="bold">{{ marketLabel }}</Text>
              </Box>
              <Box>
                <Text type="caption" color="muted">Code</Text>
                <Text type="body" weight="bold" class="fee-code">{{ form.code }}</Text>
              </Box>
              <Box>
                <Text type="caption" color="muted">Stage</Text>
                <Text type="body" weight="bold">
                  {{ FEE_LIFECYCLE_STAGE_LABEL[form.lifecycleStage] }}
                </Text>
              </Box>
              <Box>
                <Text type="caption" color="muted">Paid by</Text>
                <Text type="body" weight="bold">
                  {{ FEE_PARTY_ROLE_LABEL[form.paidByRole] }}
                </Text>
              </Box>
              <Box>
                <Text type="caption" color="muted">Paid to</Text>
                <Text type="body" weight="bold">
                  {{ FEE_PARTY_ROLE_LABEL[form.paidToRole] }}
                </Text>
              </Box>
            </Box>
          </Box>

          <Box class="grid">
            <Box class="col-12 md:col-6 mb-4">
              <FormField label="Name" required :error="visibleErrors.name">
                <Input v-model="form.name" placeholder="DLD transfer fee" />
              </FormField>
            </Box>

            <Box v-if="!isSupersede" class="col-12 md:col-6 mb-4">
              <FormField
                label="Code"
                required
                :error="visibleErrors.code"
                helper-text="Generated from the name — change it now if you need to. Immutable once created, because settled fees are traced by it."
              >
                <Input v-model="codeModel" placeholder="DLD_TRANSFER_FEE" class="fee-code" />
              </FormField>
            </Box>

            <Box v-if="!isSupersede" class="col-12 md:col-6 mb-4">
              <FormField
                label="Market"
                required
                helper-text="All markets stores no market on the fee — every market inherits it."
              >
                <Select
                  v-model="marketModel"
                  :options="marketOptions"
                  placeholder="Select a market"
                />
              </FormField>
            </Box>

            <Box v-if="!isSupersede" class="col-12 md:col-6 mb-4">
              <FormField label="Lifecycle stage" required>
                <Select
                  v-model="form.lifecycleStage"
                  :options="FEE_LIFECYCLE_STAGE_OPTIONS"
                  placeholder="Select a stage"
                />
              </FormField>
            </Box>

            <Box v-if="!isSupersede" class="col-12 md:col-6 mb-4">
              <FormField label="Paid by" required>
                <Select
                  v-model="form.paidByRole"
                  :options="FEE_PARTY_ROLE_OPTIONS"
                  placeholder="Who pays"
                />
              </FormField>
            </Box>

            <Box v-if="!isSupersede" class="col-12 md:col-6 mb-4">
              <FormField label="Paid to" required>
                <Select
                  v-model="form.paidToRole"
                  :options="FEE_PARTY_ROLE_OPTIONS"
                  placeholder="Who receives it"
                />
              </FormField>
            </Box>

            <Box v-if="!isSupersede" class="col-12 mb-4">
              <Chip :label="partyRoleLabel" color="primary" variant="tonal" icon="arrow-right" />
            </Box>

            <Box class="col-12 mb-4">
              <FormField label="What it covers" optional>
                <Textarea
                  v-model="form.description"
                  :rows="3"
                  placeholder="Explain what this fee pays for..."
                />
              </FormField>
            </Box>

            <Box class="col-12 md:col-6 mb-4">
              <FormField label="Value type" required>
                <SelectButton
                  v-model="form.valueType"
                  :options="FEE_VALUE_TYPE_OPTIONS"
                  option-label="label"
                  option-value="value"
                  class="w-full custom-select-button"
                />
              </FormField>
            </Box>

            <Box class="col-12 md:col-6 mb-4">
              <FormField :label="valueFieldLabel" required :error="visibleErrors.defaultValue">
                <NumberInput
                  v-model="form.defaultValue"
                  :mode="isPercentage ? 'decimal' : 'currency'"
                  :currency="isPercentage ? undefined : 'AED'"
                  locale="en-US"
                  :min-fraction-digits="2"
                  input-class="font-bold"
                />
              </FormField>
            </Box>

            <Box v-if="isRatePerUnit" class="col-12 md:col-6 mb-4">
              <FormField
                label="Charged per"
                required
                :error="visibleErrors.unitBasis"
                helper-text="The property measurement the rate is multiplied by. Areas are recorded in sqm."
              >
                <Select
                  v-model="unitBasisModel"
                  :options="FEE_UNIT_BASIS_OPTIONS"
                  placeholder="Select an area"
                />
              </FormField>
            </Box>

            <Box class="col-12 md:col-6 mb-4">
              <FormField
                :label="isPercentage ? 'Calculated on' : 'Charged against'"
                required
                :error="visibleErrors.valueBasis"
                :helper-text="valueBasisHelperText"
              >
                <Select
                  v-model="valueBasisModel"
                  :options="valueBasisOptions"
                  placeholder="Select a basis"
                />
              </FormField>
            </Box>

            <Box class="col-12 md:col-6 mb-4">
              <FormField label="Recurrence" required>
                <Select v-model="form.recurrence" :options="FEE_RECURRENCE_OPTIONS" />
              </FormField>
            </Box>

            <Box class="col-12 md:col-6 mb-4">
              <FormField label="VAT applicable">
                <Box flex class="align-items-center gap-3 mt-2">
                  <ToggleSwitch v-model="form.isVatApplicable" />
                  <Text type="body" weight="bold">{{ form.isVatApplicable ? 'Yes' : 'No' }}</Text>
                </Box>
              </FormField>
            </Box>

            <Box v-if="form.isVatApplicable" class="col-12 md:col-6 mb-4">
              <FormField label="VAT rate (%)" required :error="visibleErrors.vatPercentage">
                <NumberInput
                  v-model="form.vatPercentage"
                  mode="decimal"
                  locale="en-US"
                  :min-fraction-digits="2"
                />
              </FormField>
            </Box>

            <Box class="col-12 md:col-6 mb-4">
              <FormField label="Properties may override">
                <Box flex class="align-items-center gap-3 mt-2">
                  <ToggleSwitch v-model="form.isCustomisable" />
                  <Text type="body" weight="bold">
                    {{ form.isCustomisable ? 'Overridable' : 'Fixed' }}
                  </Text>
                </Box>
              </FormField>
            </Box>

            <Box class="col-12 md:col-6 mb-4">
              <FormField label="Status">
                <Box flex class="align-items-center gap-3 mt-2">
                  <ToggleSwitch v-model="form.isActive" />
                  <Text
                    type="body"
                    weight="bold"
                    :class="form.isActive ? 'text-success' : 'text-muted'"
                  >
                    {{ form.isActive ? 'Active' : 'Disabled' }}
                  </Text>
                </Box>
              </FormField>
            </Box>

            <template v-if="isSupersede">
              <Box class="col-12 md:col-6 mb-4">
                <FormField
                  label="Takes effect from"
                  optional
                  helper-text="Leave empty to apply immediately."
                >
                  <DatePicker
                    v-model="form.effectiveFrom"
                    date-format="yy-mm-dd"
                    placeholder="Apply immediately"
                    show-icon
                  />
                </FormField>
              </Box>

              <Box class="col-12 mb-4">
                <FormField label="Reason for change" required :error="visibleErrors.changeReason">
                  <Textarea
                    v-model="form.changeReason"
                    :rows="4"
                    :maxlength="500"
                    placeholder="Why is this fee changing?"
                  />
                </FormField>
              </Box>
            </template>
          </Box>

          <Box flex class="justify-content-end gap-3 mt-4 pt-5 actions-row">
            <Button label="Cancel" variant="outline" severity="secondary" @click="emit('back')" />
            <Button
              :label="isSupersede ? 'Save New Version' : 'Create Fee'"
              icon="pi pi-check"
              :loading="props.isLoading"
              @click="handleSubmit"
            />
          </Box>
        </Card>
      </Box>

      <Box class="col-12 lg:col-4">
        <Box class="policy-sidebar">
          <Text type="subheading" class="policy-heading mb-3">
            {{ isSupersede ? 'What Saving Does' : 'Before You Create' }}
          </Text>
          <Box v-if="isSupersede" flex class="flex-column gap-3">
            <Box flex class="gap-2">
              <PiIcon name="history" class="text-base mt-1 policy-icon" />
              <Text type="body" class="line-height-3">
                The version in force is closed at the effective date and a new one opens carrying
                these values. Nothing is edited in place.
              </Text>
            </Box>
            <Box flex class="gap-2">
              <PiIcon name="lock" class="text-base mt-1 policy-icon" />
              <Text type="body" class="line-height-3">
                Code, stage and the paying parties are the fee's identity — they carry over
                unchanged. Create a separate fee if those need to differ.
              </Text>
            </Box>
            <Box flex class="gap-2">
              <PiIcon name="check-circle" class="text-base mt-1 policy-icon" />
              <Text type="body" class="line-height-3">
                Fees already settled keep pointing at the version that produced them, so the reason
                you give here is what explains the difference later.
              </Text>
            </Box>
          </Box>
          <Box v-else flex class="flex-column gap-3">
            <Box flex class="gap-2">
              <PiIcon name="tag" class="text-base mt-1 policy-icon" />
              <Text type="body" class="line-height-3">
                The code is how settlements find this fee, and one market may hold only one open
                version of it. It is written from the name, editable until you create the fee, and
                fixed afterwards.
              </Text>
            </Box>
            <Box flex class="gap-2">
              <Text type="body" class="line-height-3">
                A one-time purchase cost paid by investors at primary sale is part of the offering
                price. A fee the property owner pays is deducted from the seller's proceeds instead.
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.text-muted {
  color: var(--p-text-muted-color);
}

.text-success {
  color: var(--p-success-600);
}

.animate-fade-in {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.field-label {
  display: block;
  margin-bottom: 0.5rem;
}

.fee-code {
  font-family: var(--font-mono, monospace);
}

.readonly-section {
  padding: 0.75rem;
  background: var(--p-surface-50);
  border-radius: var(--p-border-radius-md);
  border: 1px dashed var(--p-surface-200);
}

.actions-row {
  border-top: 1px solid var(--p-surface-100);
}

.policy-sidebar {
  background: var(--p-primary-50);
  border-radius: var(--p-border-radius-lg);
  padding: 1.25rem;
  border: 1px solid var(--p-primary-100);
}

.policy-icon {
  color: var(--p-primary-600);
}

:deep(.custom-select-button .p-button) {
  flex: 1;
  font-size: 0.875rem;
  font-weight: 600;
}

:deep(.custom-select-button .p-highlight) {
  background-color: var(--p-primary-600) !important;
  border-color: var(--p-primary-600) !important;
}
</style>
